import { FC, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import ScrollToTop from '@/components/Buttons/ScrollToTop';
import Container from '@/components/Container/Container';
import EmptyState from '@/components/EmptyState/EmptyState';
import Filter from '@/components/Filter/Filter';
import Footer from '@/components/Footer/Footer';
import FooterBanner from '@/components/FooterBanner/FooterBanner';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import { LANGUAGES } from '@/lib/constants';
import { fetchAPI } from '@/lib/fetchAPI';
import { translate } from '@/lib/translate';
import Story from '@/modules/success-stories/StoryItem';
import { GlobalContext } from '@/pages/_app';

const StoriesPage: FC<any> = ({ lang, posts, emptyState, defaultFilters }) => {
	const { device } = useContext(GlobalContext);
	const [visiblePosts, setVisiblePosts] = useState<any[]>([]);
	const [filters, setFilters] = useState<any[]>([]);
	const [mobilePopupStatus, setMobilePopupStatus] = useState(false);
	const [activeTabMobile, setActiveTabMobile] = useState(0);

	const getOptionIndex = (currentItem, optionText) =>
		currentItem.optionName === optionText;

	const changeOption = (optionNewStatus, filterIndex, optionText) => {
		const updatedFilters = [...filters];

		updatedFilters[filterIndex].selectAll = false;

		const optionIndex = updatedFilters[filterIndex].options.findIndex(
			(currentItem) => getOptionIndex(currentItem, optionText),
		);

		updatedFilters[filterIndex].options[optionIndex].active = optionNewStatus;

		if (!optionNewStatus) {
			const activeFilters = updatedFilters[filterIndex].options.filter(
				(option) => option.active,
			);

			if (activeFilters.length === 0)
				updatedFilters[filterIndex].selectAll = true;
		}

		setFilters(updatedFilters);
		filterPosts(updatedFilters);
	};

	const filterPosts = (updatedFilters: any[]) => {
		const newPosts = posts.filter((post) => {
			let passesAllFilters = false;

			for (const filter in updatedFilters) {
				let passesCurrentFilter = false;

				for (const option in updatedFilters[filter].options) {
					if (updatedFilters[filter].selectAll) {
						passesCurrentFilter = true;

						break;
					}

					if (updatedFilters[filter].options[option].active) {
						const optionName =
							updatedFilters[filter].options[option].optionName;
						const filterType = updatedFilters[filter].type.toLowerCase();

						if (post[filterType].includes(optionName)) {
							passesCurrentFilter = true;

							break;
						}
					}
				}

				if (passesCurrentFilter) passesAllFilters = true;
				else {
					passesAllFilters = false;

					break;
				}
			}

			return passesAllFilters && post;
		});

		setVisiblePosts(newPosts);
	};

	const selectAllOptions = (filterIndex) => {
		const updatedFilters = [...filters];

		updatedFilters[filterIndex].selectAll = true;

		for (const k in updatedFilters[filterIndex].options) {
			updatedFilters[filterIndex].options[k].active = false;
		}

		setFilters(updatedFilters);
		filterPosts(updatedFilters);
	};

	const clearAll = () => {
		const newFilters = defaultFilters?.map((filter) => ({
			...filter,
			selectAll: true,
			options: filter?.values?.map((option) => ({
				optionName: option,
				active: false,
			})),
		}));

		setFilters(newFilters);
		setVisiblePosts(posts);
	};

	const openMobilePopup = () => setMobilePopupStatus(true);
	const closeMobilePopup = () => setMobilePopupStatus(false);

	useEffect(() => {
		const initialPosts = posts?.filter((post) =>
			post?.url === 'wwf' && lang === 'ru' ? false : true,
		);

		const newFilters = defaultFilters?.map((filter) => ({
			...filter,
			selectAll: true,
			options: filter?.values?.map((option) => ({
				optionName: option,
				active: false,
			})),
		}));

		setVisiblePosts(initialPosts);
		setFilters(newFilters);
	}, [posts, defaultFilters]);

	return (
		<Page>
			<Seo
				url="/success-stories"
				title={translate(lang, {
					en: 'Viber Success Stories',
					ru: 'Истории успеха Viber',
					ua: 'Історії успіху Viber',
				})}
				description={translate(lang, {
					en: 'Viber presents partner success stories from different region, industry and Viber product: advertising, sticker, chatbot, business messages. Get inspired and start your success story today!',
					ru: 'Viber представляет истории успеха партнеров из разных регионов, отраслей и продуктов Viber: реклама, стикеры, чат-боты, деловые сообщения. Получите вдохновение и начните свою историю успеха уже сегодня!',
					ua: 'Viber представляє історії успіху партнерів з різних регіонів, галузей та продуктів Viber: реклама, наклейки, чат-бот, ділові повідомлення. Отримайте натхнення та почніть свою історію успіху вже сьогодні!',
				})}
			/>

			<Navigation lang={lang} />

			<Wrapper>
				<Top
					loading="lazy"
					alt="Gradient"
					src="/images/success-stories/gradient.png"
				/>

				<Container>
					<Heading>
						<Left>
							<Title $lang={lang}>
								{translate(lang, {
									en: 'Success stories',
									ru: 'Истории успеха',
									ua: 'Історії успіху',
								})}
							</Title>

							{device === 'desktop' && (
								<Description>
									{translate(lang, {
										en: 'Discover how Viber solutions are helping brands grow',
										ru: 'Узнайте как Viber помог брендам достичь успеха',
										ua: 'Дізнайтеся як Viber допоміг брендам досягти успіху',
									})}
								</Description>
							)}
						</Left>

						<Right>
							<FiltersMobile>
								<FilterMobile onClick={openMobilePopup}>
									{translate(lang, {
										en: 'Filter',
										ru: 'Фильтры',
										ua: 'Фільтри',
									})}
								</FilterMobile>

								<FilterPopup $active={mobilePopupStatus}>
									<FilterPopupRow>
										{filters.map((filter, filterIndex) => (
											<Filter
												key={`story_filter_mobile_${filterIndex}`}
												activeMobile={activeTabMobile === filterIndex}
												filterIndex={filterIndex}
												name={filter.name}
												options={filter.options}
												selectAllStatus={filter.selectAll}
												selectAll={selectAllOptions}
												changeActiveOptions={changeOption}
												changeActiveTabMobile={() =>
													setActiveTabMobile(filterIndex)
												}
											/>
										))}
									</FilterPopupRow>

									<ClosePopupButton onClick={closeMobilePopup}>
										<img src="/images/close.png" alt="Close" />
									</ClosePopupButton>

									<MobileButtonsRow>
										<MobileClear onClick={clearAll}>RESET ALL</MobileClear>

										<MobileApply onClick={closeMobilePopup}>
											APPLY FILTERS
										</MobileApply>
									</MobileButtonsRow>
								</FilterPopup>
							</FiltersMobile>

							<Filters>
								<ClearAll onClick={clearAll}>
									{translate(lang, {
										en: 'Clear all filters',
										ru: 'Очистить все',
										ua: 'Очистити',
									})}
								</ClearAll>

								<FiltersRow>
									{filters.map((filter, filterIndex) => (
										<Filter
											key={`story_filter_desktop_${filterIndex}`}
											name={filter.name}
											options={filter.options}
											filterIndex={filterIndex}
											selectAll={selectAllOptions}
											selectAllStatus={filter.selectAll}
											changeActiveOptions={changeOption}
										/>
									))}
								</FiltersRow>
							</Filters>
						</Right>
					</Heading>

					{device === 'mobile' && (
						<SubtitleMobile>
							<FirstRow>
								{translate(lang, {
									en: 'Discover how Viber solutions',
									ru: 'Узнайте как Viber помог',
									ua: 'Дізнайтесь, як Viber допомагає',
								})}
							</FirstRow>

							<SecondRow>
								{translate(lang, {
									en: 'are helping brands grow',
									ru: 'брендам достичь успеха',
									ua: 'брендам зростати',
								})}
							</SecondRow>
						</SubtitleMobile>
					)}

					{visiblePosts?.length > 0 ? (
						<Row>
							{visiblePosts.map((story, index) => (
								<Column key={`story_visible_col_${index}`}>
									<Story data={story} />
								</Column>
							))}
						</Row>
					) : (
						<EmptyState data={emptyState} />
					)}

					<ScrollToTop />
				</Container>

				<Bottom
					loading="lazy"
					alt="Gradient"
					src="/images/success-stories/gradient.png"
				/>
			</Wrapper>

			<FooterBanner
				anotherSite
				successStories
				title={translate(lang, {
					en: 'Join our newsletter',
					ru: 'Подпишитесь на нашу рассылку',
					ua: 'Підписуйтесь на розсилку',
				})}
				cta={{
					text: translate(lang, {
						en: 'SUBSCRIBE',
						ru: 'ПОДПИСАТЬСЯ',
						ua: 'ПІДПИСАТИСЯ',
					}),
					link: translate(lang, {
						en: 'https://b2b.viber.com/newsletter-opt-in',
						ru: 'https://b2b.viber.com/ru/cis-newsletter-opt-in',
						ua: 'https://b2b.viber.com/newsletter-opt-in',
					}),
				}}
				image={{
					link: translate(lang, {
						en: '/images/success-stories/footerPhone.webp',
						ru: '/images/success-stories/footerBanner_ru.webp',
						ua: '/images/success-stories/footerBanner_uk.webp',
					}),
					alt: translate(lang, {
						en: 'Viber Success Stories 1',
						ru: 'Истории успеха Viber 1',
						ua: 'Історії успіху Viber 1',
					}),
				}}
			/>

			<Footer lang={lang} />
		</Page>
	);
};

const Page = styled.div`
	max-width: 100%;
	position: relative;
	overflow: hidden;
`;

const Top = styled.img`
	position: absolute;
	top: -60px;
	z-index: 10;

	@media (max-width: 800px) {
		display: none;
	}
`;

const Bottom = styled.img`
	position: absolute;
	right: -10%;
	top: 85%;
	transform: rotate(185deg);
	z-index: 10;

	@media (max-width: 700px) {
		display: none;
	}
`;

const Wrapper = styled.div`
	margin-top: 140px;
	margin-bottom: 80px;
	position: relative;

	@media (max-width: 1024px) {
		margin-bottom: 140px;
	}

	@media (max-width: 800px) {
		margin-bottom: 80px;
		margin-top: 115px;
	}

	@media (max-width: 660px) {
		margin-top: 90px;
	}
`;

const Heading = styled.div`
	margin-bottom: 80px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: relative;
	z-index: 300;

	@media (max-width: 890px) {
		flex-direction: column;
		align-items: flex-end;
	}

	@media (max-width: 800px) {
		margin-bottom: 44px;
	}

	@media (max-width: 600px) {
		flex-direction: row;
		align-items: flex-end;
		margin-bottom: 0;
	}
`;

const Left = styled.div`
	@media (max-width: 890px) {
		width: 100%;
	}

	@media (max-width: 600px) {
		align-self: flex-start;
		margin-bottom: 20px;
	}
`;

const Title = styled.h1<{ $lang: string }>`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	color: var(--color-purple);
	margin-bottom: 25px;

	@media (max-width: 1000px) {
		font-size: 50px;
		line-height: 60px;
	}

	@media (max-width: 800px) {
		font-size: 40px;
		line-height: 50px;
	}

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 40px;
		margin-bottom: 0;

		${({ $lang }) =>
			$lang === 'ua' &&
			css`
				width: 80%;
			`}
	}

	@media (max-width: 370px) {
		font-size: 32px;
		line-height: 36px;
	}
`;

const Description = styled.div`
	font-family: var(--font-sans);
	font-size: 28px;
	line-height: 35px;
	color: var(--color-black);
	display: block;

	@media (max-width: 600px) {
		display: none;
	}
`;

const SubtitleMobile = styled.div`
	display: none;

	@media (max-width: 600px) {
		display: block;
		margin-bottom: 40px;
	}
`;

const FirstRow = styled.span`
	display: none;

	@media (max-width: 600px) {
		display: block;
		font-family: var(--font-sans);
		font-size: 22px;
		line-height: 30px;
		color: var(--color-black);
	}

	@media (max-width: 370px) {
		font-size: 18px;
		line-height: 22px;
	}

	@media (max-width: 300px) {
		font-size: 16px;
		line-height: 20px;
	}
`;

const SecondRow = styled.span`
	display: none;

	@media (max-width: 600px) {
		display: block;
		font-family: var(--font-sans);

		font-size: 22px;
		line-height: 30px;
		color: var(--color-black);
	}

	@media (max-width: 370px) {
		font-size: 18px;
		line-height: 22px;
	}

	@media (max-width: 300px) {
		font-size: 16px;
		line-height: 20px;
	}
`;

const Right = styled.div`
	@media (max-width: 600px) {
		align-self: flex-start;
	}
`;

const FiltersMobile = styled.div`
	display: none;

	@media (max-width: 600px) {
		display: block;
		margin-top: 2px;
	}
`;

const FilterMobile = styled.div`
	width: 120px;
	max-width: 120px;
	background-color: var(--color-white-30);
	border-radius: 12px;
	font-family: var(--font-sans);
	font-size: 16px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	padding: 7px 4px;
`;

const FilterPopup = styled.div<{ $active: boolean }>`
	display: none;
	position: fixed;
	background-color: var(--color-white-40);
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	padding-top: 65px;
	z-index: 300;

	${({ $active }) =>
		$active &&
		css`
			display: flex;
			flex-direction: column;
		`}
`;

const FilterPopupRow = styled.div`
	display: flex;
	margin-top: 47px;
	position: relative;

	@media (max-width: 600px) {
		justify-content: center;
		border-radius: 30px;
		padding-left: 5px;

		.filter {
			margin-right: 5px;
		}

		.filter:first-child .filter__select {
			border-top-left-radius: 14px;
			border-bottom-left-radius: 14px;
		}

		.filter:last-child {
			.filter__select {
				border-top-right-radius: 14px;
				border-bottom-right-radius: 14px;
			}
		}
	}
`;

const ClosePopupButton = styled.div`
	position: absolute;
	top: 66px;
	right: 5px;
	padding: 10px;

	img {
		width: 22px;
		height: 22px;
	}
`;

const Filters = styled.div`
	@media (max-width: 600px) {
		display: none;
	}
`;

const ClearAll = styled.button`
	display: block;
	cursor: pointer;
	margin-left: auto;
	margin-right: 4px;
	margin-bottom: 15px;
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	text-align: right;
	color: var(--color-purple-50);
	outline: none;
	opacity: 0.6;
	appearance: none;
	background-color: transparent;
`;

const FiltersRow = styled.div`
	display: flex;
	border-radius: 30px;

	.filter {
		margin-right: 5px;
	}

	.filter:first-child .filter__select {
		border-top-left-radius: 14px;
		border-bottom-left-radius: 14px;
	}

	.filter:last-child {
		.filter__select {
			border-top-right-radius: 14px;
			border-bottom-right-radius: 14px;
		}

		.filter__dropdown {
			left: auto;
			transform: none;
			right: -20px;
		}
	}
`;

const Column = styled.div`
	width: 400px;
	max-width: calc(33.3% - 40px);
	margin-right: 60px;
	margin-bottom: 80px;

	@media (min-width: 1600px) {
		width: calc(33.3% - 30px);
		max-width: calc(33.3% - 30px);
		margin-right: 45px;
		margin-bottom: 50px;
	}

	@media (max-width: 1150px) {
		width: 400px;
		max-width: calc(33.3% - 20px);
		margin-right: 30px;
		margin-bottom: 50px;
	}

	@media (max-width: 1024px) {
		width: calc(50% - 40px);
		max-width: calc(50% - 40px);
		margin-right: 0;
		margin-bottom: 50px;
	}

	@media (max-width: 800px) {
		max-width: calc(50% - 20px);
		width: calc(50% - 20px);
		margin-right: 0;
		margin-bottom: 47px;
	}

	@media (max-width: 660px) {
		max-width: calc(50% - 20px);
		width: calc(50% - 20px);
		margin-right: 0;
		margin-bottom: 27px;
	}

	@media (max-width: 550px) {
		max-width: calc(50% - 8px);
		width: calc(50% - 8px);
		margin-right: 0;
		margin-bottom: 27px;
	}

	@media (max-width: 535px) {
		max-width: 100%;
		width: 100%;
		margin-right: 0;
		margin-bottom: 40px;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	justify-content: flex-start;
	position: relative;
	z-index: 30;

	@media (max-width: 1024px) {
		justify-content: space-between;
	}

	${Column}:nth-child(3n) {
		margin-right: 0;
	}
`;

const MobileButtonsRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: auto;
	margin-bottom: 4vh;
`;

const MobileClear = styled.div`
	background-color: var(--color-white);
	border-radius: 12px;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 15px;
	text-transform: uppercase;
	color: var(--color-purple);
	text-align: center;
	padding-top: 17px;
	padding-bottom: 17px;
	padding-left: 40px;
	padding-right: 40px;

	@media (max-width: 360px) {
		padding-left: 30px;
		padding-right: 30px;
		padding-top: 14px;
		padding-bottom: 14px;
		font-size: 13px;
	}
`;

const MobileApply = styled.div`
	border-radius: 12px;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 15px;
	background-color: var(--color-purple);
	color: var(--color-white);
	margin-left: 15px;
	text-transform: uppercase;
	text-align: center;
	padding-top: 17px;
	padding-bottom: 17px;
	padding-left: 40px;
	padding-right: 40px;

	@media (max-width: 360px) {
		padding-left: 30px;
		padding-right: 30px;
		padding-top: 14px;
		padding-bottom: 14px;
		font-size: 13px;
	}
`;

export async function getStaticProps({ params }) {
	const { lang } = params;
	const [allStories, industries, products, regions, filters] =
		await Promise.all([
			fetchAPI('/stories', {
				lang,
				query: 'sort[0]=id:desc',
				populate: ['story', 'preview', 'industries', 'products', 'regions'],
			}),
			fetchAPI('/stories-industries', { lang }),
			fetchAPI('/stories-products', { lang }),
			fetchAPI('/stories-regions', { lang }),
			fetchAPI('/filters-empty-state', {
				lang,
				populate: ['emptyState', 'emptyState.cta', 'emptyState.image'],
			}),
		]);

	const defaultFilters = [
		{
			type: 'Industry',
			name: translate(lang, {
				en: 'Industry',
				ru: 'Индустрия',
				ua: 'Індустрія',
			}),
			values: industries?.data?.map(({ industry }) => industry ?? null),
		},
		{
			type: 'Regions',
			name: translate(lang, {
				en: 'Regions',
				ru: 'Регион',
				ua: 'Регіон',
			}),
			values: regions?.data?.map(({ region }) => region ?? null),
		},
		{
			type: 'Products',
			name: translate(lang, {
				en: 'Products',
				ru: 'Продукт',
				ua: 'Продукт',
			}),
			values: products?.data?.map(({ product }) => product ?? null),
		},
	];

	const posts =
		allStories?.data?.map((story) => ({
			...story,
			regions: story?.regions?.map(({ region }) => region ?? null),
			industry: story?.industries?.map(({ industry }) => industry ?? null),
			products: story?.products?.map(({ product }) => product ?? null),
		})) ?? [];

	return {
		props: {
			lang,
			posts,
			defaultFilters,
			emptyState: filters?.data?.emptyState?.[0],
		},
	};
}

export async function getStaticPaths() {
	const paths = LANGUAGES.map(({ code }) => ({ params: { lang: code } }));

	return { paths, fallback: false };
}

export default StoriesPage;
